import { useRef, type ChangeEvent, type InputHTMLAttributes } from 'react'
import { Box, TextField, type TextFieldProps } from '@mui/material'
import { formatDate } from '@/utils/date'

type NativeDateInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'disabled'
>

type DateTextFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
  value?: string | null
  onChange: (value: string) => void
  nativeInputProps?: NativeDateInputProps
}

export default function DateTextField({
  value,
  onChange,
  disabled,
  fullWidth,
  inputProps,
  InputLabelProps,
  nativeInputProps,
  sx,
  ...textFieldProps
}: DateTextFieldProps) {
  const nativeInputRef = useRef<HTMLInputElement | null>(null)
  const { style: nativeStyle, ...restNativeInputProps } = nativeInputProps ?? {}

  const handleNativeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  const handleOpenPicker = () => {
    if (disabled) return
    const input = nativeInputRef.current
    if (!input) return
    input.focus()
    input.showPicker?.()
  }

  return (
    <Box
      onClick={handleOpenPicker}
      sx={[
        {
          position: 'relative',
          width: fullWidth ? '100%' : undefined,
          cursor: disabled ? 'default' : 'pointer',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <TextField
        {...textFieldProps}
        fullWidth={fullWidth}
        disabled={disabled}
        value={formatDate(value)}
        InputLabelProps={{ ...InputLabelProps, shrink: true }}
        inputProps={{ ...inputProps, placeholder: 'DD-MMM-YYYY', readOnly: true, tabIndex: -1 }}
        sx={{ width: '100%' }}
      />
      <input
        ref={nativeInputRef}
        {...restNativeInputProps}
        type="date"
        value={value ?? ''}
        disabled={disabled}
        onChange={handleNativeChange}
        tabIndex={disabled ? -1 : (nativeInputProps?.tabIndex ?? 0)}
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          cursor: disabled ? 'default' : 'pointer',
          width: '100%',
          height: '100%',
          zIndex: 1,
          ...nativeStyle,
        }}
      />
    </Box>
  )
}
